import {IMAGE_CONFIG} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
} from '@angular/material/core';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideRouter, withInMemoryScrolling, withNavigationErrorHandler} from '@angular/router';
import {CovDateAdapter, CovToastComponent} from '@cov/ui-angular';
import {provideTransloco} from '@jsverse/transloco';
import {provideTranslocoMessageformat} from '@jsverse/transloco-messageformat';
import {enGB} from 'date-fns/locale/en-GB';
import {CookieService} from 'ngx-cookie-service';
import {provideToastr, ToastrService} from 'ngx-toastr';
import {Observable, switchMap, take} from 'rxjs';
import {appRoutes} from './app.routes';
import {BREAKPOINTS_ANGULAR_IMAGE, DEFAULT_LANGUAGE} from './shared/constants';
import {AuthInterceptor} from './shared/interceptors/auth-interceptor';
import {ResponseInterceptor} from './shared/interceptors/response-interceptor';
import {AuthService} from './shared/services/auth.service';
import {TranslocoHttpLoader} from './transloco-loader';

import {MAT_DATE_FNS_FORMATS} from '@angular/material-date-fns-adapter';
import {Language} from './enums/language.enum';
import {SIDE_PANEL_DATA} from './shared/token/side-panel.token';
import {AppService} from './shared/services/app.service';
import {LanguageInterceptor} from './shared/interceptors/language-interceptor';
import {LocaleService} from './shared/services/locale.service';
import {CovErrorHandler} from './shared/error/cov-error-handler.service';
import {recoverFromChunkLoadError} from './shared/utils/chunk-load-recovery.utils';

export function initializeApp(
  authService: AuthService,
  appService: AppService,
  localeService: LocaleService,
): () => Observable<any> {
  return () =>
    authService.init().pipe(
      switchMap(() => appService.init()),
      take(1),
      switchMap(() => localeService.isReady()),
    );
}

export const appConfig: ApplicationConfig = {
  providers: [
    // HTTP client and interceptors must be registered before app initializer
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    // App initializer runs after HTTP infrastructure is set up
    provideAppInitializer(() =>
      initializeApp(inject(AuthService), inject(AppService), inject(LocaleService))(),
    ),
    provideZoneChangeDetection({eventCoalescing: true}),
    // when navigating, the page is scrolled to top
    provideRouter(
      appRoutes,
      withInMemoryScrolling({scrollPositionRestoration: 'top'}),
      withNavigationErrorHandler((error) => recoverFromChunkLoadError(error)),
    ),
    provideAnimationsAsync(),
    CookieService,
    ToastrService,
    provideToastr({
      toastComponent: CovToastComponent,
    }),
    {
      provide: ErrorHandler,
      useClass: CovErrorHandler,
    },
    // default is "en"
    {provide: MAT_DATE_LOCALE, useValue: enGB},
    // date-fns specific formats
    {provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS},
    // customer adapter which handles first day of week and view format of short weekdays
    {provide: DateAdapter, useClass: CovDateAdapter},
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {appearance: 'outline', floatLabel: 'always'},
    },
    {provide: SIDE_PANEL_DATA, useValue: null},
    {provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: {disabled: true}},
    {
      provide: IMAGE_CONFIG,
      useValue: {
        breakpoints: BREAKPOINTS_ANGULAR_IMAGE,
      },
    },
    provideTransloco({
      config: {
        availableLangs: Object.values(Language),
        fallbackLang: DEFAULT_LANGUAGE,
        missingHandler: {
          allowEmpty: false,
          useFallbackTranslation: true,
        },
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
  ],
};
